import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ACCESS_CONFIG = {
  accessible: {
    label: 'Access Granted',
    icon: 'FolderOpen',
    colorClass: 'accessible',
    tooltipColor: 'var(--color-success)',
  },
  restricted: {
    label: 'Restricted',
    icon: 'Lock',
    colorClass: 'restricted',
    tooltipColor: 'var(--color-error)',
  },
  pending: {
    label: 'Pending Review',
    icon: 'Clock',
    colorClass: 'pending',
    tooltipColor: 'var(--color-warning)',
  },
};

export default function DocumentAccessIndicator({
  accessStatus = 'accessible', // 'accessible' | 'restricted' | 'pending'
  documentCount = 0,
  updatedCount = 0,
  lastUpdated = null,
  onRequestAccess,
  onViewDocuments,
  compact = false,
}) {
  const [showTooltip, setShowTooltip] = useState(false);

  const config = ACCESS_CONFIG?.[accessStatus] || ACCESS_CONFIG?.accessible;

  const handleClick = (e) => {
    e?.stopPropagation();
    if (accessStatus === 'accessible' && onViewDocuments) {
      onViewDocuments();
    } else if (accessStatus === 'restricted' && onRequestAccess) {
      onRequestAccess();
    }
  };

  if (compact) {
    return (
      <div className="relative inline-flex">
        <button
          onClick={handleClick}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          className={`doc-indicator ${config?.colorClass} transition-all duration-250 hover:opacity-80 min-h-touch min-w-touch flex items-center justify-center`}
          aria-label={`Documents: ${config?.label}${documentCount > 0 ? `, ${documentCount} files` : ''}`}
        >
          <Icon name={config?.icon} size={14} />
          {documentCount > 0 && (
            <span className="ml-1">{documentCount > 99 ? '99+' : documentCount}</span>
          )}
          {updatedCount > 0 && (
            <span
              className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full text-white"
              style={{ fontSize: '0.625rem', background: 'var(--color-primary)', color: 'var(--color-accent-foreground)' }}
            >
              {updatedCount}
            </span>
          )}
        </button>
        {showTooltip && (
          <div
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-popover border border-border rounded-md shadow-md whitespace-nowrap pointer-events-none"
            style={{ zIndex: 'var(--z-dropdown)' }}
          >
            <p className="font-caption font-medium text-foreground text-sm">{config?.label}</p>
            {documentCount > 0 && (
              <p className="font-caption text-muted-foreground" style={{ fontSize: '0.75rem' }}>
                {documentCount} document{documentCount !== 1 ? 's' : ''}
                {updatedCount > 0 && ` · ${updatedCount} new`}
              </p>
            )}
            {lastUpdated && (
              <p className="font-caption text-muted-foreground" style={{ fontSize: '0.75rem' }}>
                Updated {lastUpdated}
              </p>
            )}
            {accessStatus === 'restricted' && (
              <p className="font-caption mt-1" style={{ fontSize: '0.75rem', color: 'var(--color-primary)' }}>
                Click to request access
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full indicator
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleClick}
        className={`doc-indicator ${config?.colorClass} transition-all duration-250 hover:opacity-80 min-h-touch`}
        aria-label={`Data Room: ${config?.label}`}
      >
        <Icon name={config?.icon} size={14} />
        <span>{config?.label}</span>
      </button>
      {documentCount > 0 && (
        <span className="font-data text-muted-foreground" style={{ fontSize: '0.75rem' }}>
          {documentCount} file{documentCount !== 1 ? 's' : ''}
        </span>
      )}
      {updatedCount > 0 && (
        <span
          className="doc-indicator"
          style={{
            background: 'rgba(59, 76, 76, 0.15)',
            color: '#92600A',
            fontSize: '0.6875rem',
          }}
        >
          <Icon name="Bell" size={11} />
          {updatedCount} new
        </span>
      )}
      {accessStatus === 'restricted' && onRequestAccess && (
        <button
          onClick={(e) => { e?.stopPropagation(); onRequestAccess(); }}
          className="font-caption text-primary underline transition-all duration-250 hover:opacity-70"
          style={{ fontSize: '0.75rem' }}
        >
          Request Access
        </button>
      )}
    </div>
  );
}